import React, { useState, useEffect } from "react";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import { Button } from "@/components/ui/Button";
import TextAreaBox from "@/components/ui/TextAreaBox";
import ModalLayout from "@/components/layout/ModalLayout";
import { toast } from "sonner";
import ModalHeader from "@/components/ui/ModalHeader";
import SearchModal from "./Search";
import { useItemCodeSearch, useUpdateItemCode } from "@/hooks/standard/st_item_code/useItemCode";
import { ItemCode } from "@/interface/standard/st_item_code";
import { useCodeListByItemGroupCode } from "@/hooks/standard/st_code/useCode";


interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  itemData?: ItemCode;
}

export interface ItemCodeData {
  itemCode: string;
  useStatus: string;
  itemCategory: string;
  note: string;
  searchType: "itemName" | "casNumber";
  searchQuery: string;
  itemName: string;
  casNumber: string;
  sfsgwd: string;
  itemGroupCode: string;
  specification: string;
  unit: string;
}

export default function UpdateModal({ isOpen, onClose, onUpdate, itemData }: UpdateModalProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchType, setSearchType] = useState<"itemName" | "casNumber">("itemName");
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const updateMutation = useUpdateItemCode();
  const [formData, setFormData] = useState<ItemCodeData>({
    itemCode: "",
    useStatus: "사용",
    itemCategory: "",
    note: "",
    searchType: "itemName",
    searchQuery: "",
    itemName: "",
    casNumber: "",
    sfsgwd: "",
    itemGroupCode: "",
    specification: "",
    unit: "",
  });

  const { data: bigCategoryData } = useCodeListByItemGroupCode();

  // itemData가 변경되면 폼 데이터 업데이트
  useEffect(() => {
    if (itemData) {
      setFormData({
        itemCode: String(itemData.stItemCode || ""),
        useStatus: itemData.stItemUse || "1",
        itemCategory: itemData.stItemGroupCode || "",
        note: itemData.stItemRemark || "",
        searchType: "itemName",
        searchQuery: itemData.stItemName || "",
        itemName: itemData.stItemName || "",
        casNumber: itemData.stItemCasNo || "",
        sfsgwd: String(itemData.sfsgwd || ""),
        specification: itemData.stItemSize || "",
        itemGroupCode: itemData.stItemGroupCode || "",
        unit: itemData.stItemUnit || "",
      });
    }
  }, [itemData]);


  // 사용여부 옵션
  const useStatusOptions = [
    { value: "1", label: "사용" },
    { value: "0", label: "미사용" },
  ];

  const handleInputChange = (field: keyof ItemCodeData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async () => {
    // 필수 필드 검증
    if (!formData.useStatus.trim()) {
      toast.warning("사용여부를 선택해주세요.");
      return;
    }

    if (!formData.itemCategory.trim()) {
      toast.warning("품목구분을 선택해주세요.");
      return;
    }

    try {
      // API 호출 - 백엔드 필드명에 맞게 변환
      await updateMutation.mutateAsync({
        stItemCode: itemData?.stItemCode || "",
        data: {
          stItemCode: itemData?.stItemCode || "",
          stItemName: formData.itemName,
          stItemCasNo: formData.casNumber,
          stItemUse: formData.useStatus,
          stItemGroupCode: formData.itemCategory,
          stItemSize: formData.specification,
          stItemUnit: formData.unit,
          stItemRemark: formData.note,
        },
      });
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error("수정 실패:", error);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // 페이지 상태 추가
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  
  const { data: hookData, isLoading: isSearchLoading } = useItemCodeSearch({
    search: searchKeyword,
    searchType: searchType,
    page: currentPage,
    size: 20,
    enabled: searchEnabled || isLoadingMore,
  });

  const source = hookData;
  const newRows = Array.isArray(source) ? source : (source?.content ?? []);
  
  // 데이터 누적 로직
  useEffect(() => {
    if (!hookData) return;
    const nextTotal =
      (hookData as any)?.totalElements ??
      (newRows.length > 0 && (newRows[0] as any)?.totalCount ? (newRows[0] as any).totalCount : 0);

    if (newRows.length > 0) {
      if (currentPage === 0) {
        setAllData(newRows);
        setTotalElements(nextTotal || 0);
      } else {
        setAllData(prev => [...prev, ...newRows]);
        setTotalElements(prev => (nextTotal ? nextTotal : prev));
      }
    } else if (currentPage === 0) {
      setAllData([]);
      setTotalElements(0);
    }

    setSearchEnabled(false);
    setIsLoadingMore(false);
  }, [hookData, currentPage, newRows]);
  
  // 무한스크롤을 위한 데이터
  const rows = allData;
  
  // totalCount 확인
  const totalCount = rows.length > 0 && rows[0].totalCount ? rows[0].totalCount : 0;
  const hasMore = totalCount > rows.length;

  // 검색 결과 선택
  const handleSearchSelect = (item: any) => {
    setFormData(prev => ({
      ...prev,
      itemName: item.stItemName || item.chemNameKor || "",
      casNumber: item.stItemCasNo || item.casNo || "",
      sfsgwd: item.sfsgwd || "",
      specification: item.stItemSize || item.specification || "",
      unit: item.stItemUnit || item.unit || "",
    }));
    setIsSearchOpen(false);
  };

  // 검색 테이블 컬럼
  const searchColumns = [
    { key: "chemNameKor", header: "품목명", bodyAlign: "left" as const, headerAlign: "center" as const },
    { key: "casNo", header: "cas번호", bodyAlign: "center" as const, headerAlign: "center" as const },
    { key: "sfsgwd", header: "유해위험분류", bodyAlign: "center" as const, headerAlign: "center" as const }
  ];

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} title="품목코드 수정">
      {/* 기본 정보 섹션 */}
      <div className="rounded-lg">
        <ModalHeader title="기본 정보" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <InputBox
              required={true}
              label="품목코드"
              value={formData.itemCode}
              onChange={e => handleInputChange("itemCode", e.target.value)}
              placeholder="입력"
              className="w-full"
              readOnly
            />
          </div>
          
          <div>
            <SelectBox
              label="사용여부"
              value={formData.useStatus}
              onChange={value => handleInputChange("useStatus", value)}
              options={useStatusOptions}
              placeholder="선택"
              className="w-full"
            />
          </div>
          
          <div>
            <SelectBox
              required={true}
              label="품목구분"
              value={formData.itemCategory}
              onChange={value => handleInputChange("itemCategory", value)}
              options={bigCategoryData
              ?.map((item: any) => ({
                value: item.code,
                label: item.name,
              })) || []}
              placeholder="선택"
              className="w-full"
            />
          </div>
        </div>
        {/* 비고 필드 */}
        <div className="mt-4 mb-4">
          <TextAreaBox
            label="비고"
            value={formData.note}
            onChange={e => handleInputChange("note", e.target.value)}
            placeholder="입력"
            className="w-full"
            rows={1}
          />
        </div>
      </div>
      {/* 상세 정보 섹션 */}
      <div className="rounded-lg">
        <ModalHeader title="상세 정보" />
        {/* 품목 상세 정보 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputBox
              label="품목명"
              value={formData.itemName}
              onChange={e => handleInputChange("itemName", e.target.value)}
              className="w-full"
              placeholder="입력"
            />
          </div>
          
          <div>
            <InputBox
              label="CAS 번호"
              value={formData.casNumber}
              onChange={e => handleInputChange("casNumber", e.target.value)}
              className="w-full"
              readOnly
            />
          </div>
          
          <div>
            <InputBox
              label="유해위험분류"
              value={formData.sfsgwd}
              onChange={e => handleInputChange("sfsgwd", e.target.value)}
              className="w-full"
              readOnly
            />
          </div>
        </div>

        {/* 규격 및 단위 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InputBox
              label="규격"
              value={formData.specification}
              onChange={e => handleInputChange("specification", e.target.value)}
              placeholder="입력"
              className="w-full"
            />
          </div>
          
          <div>
            <InputBox
              label="단위"
              value={formData.unit}
              onChange={e => handleInputChange("unit", e.target.value)}
              placeholder="입력"
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* 모달 푸터 */}
      <div className="flex items-center gap-3 mt-6">
        <div className="flex justify-start w-full">
          <Button variant="white" onClick={handleCancel} className="py-[10px]">
            취소
          </Button>
        </div>
        <div className="flex justify-end w-full">
          <Button variant="primary" onClick={handleUpdate} className="py-[10px]">
            수정
          </Button>
        </div>
      </div>

      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelect={handleSearchSelect}
        title="품목 검색"
        columns={searchColumns}
        data={rows as any[]}
        isLoadingInitial={isSearchLoading && rows.length === 0}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        searchOptions={[
          { value: "chemNameKor", label: "품목명" },
          { value: "casNo", label: "cas번호" },
          { value: "chemId", label: "화학물질 ID" },
        ]}
        onSearch={(keyword) => {
          setSearchType(searchType);
          setSearchKeyword(keyword);
          setIsLoadingMore(false);
          setCurrentPage(0);
          setAllData([]);
          setSearchEnabled(true);
        }}
        onSearchButtonClick={(keyword) => {
          setSearchType(searchType);
          setSearchKeyword(keyword);
          setIsLoadingMore(false);
          setCurrentPage(0);
          setAllData([]);
          setSearchEnabled(true);
        }}
        onInfiniteScroll={() => {
          if (hasMore) {
            setIsLoadingMore(true);
            setCurrentPage(prev => prev + 1);
          }
        }}
        initialSearchKeyword={formData.searchQuery}
      />
    </ModalLayout>
  );
}

